import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react'

import { FOCUS_RING } from './focusRing.ts'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary'
export type ButtonTone = 'brand' | 'neutral' | 'danger'
export type ButtonSize = 'icon' | 'small' | 'medium' | 'large'

export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  children?: ReactNode
  variant?: ButtonVariant
  tone?: ButtonTone
  size?: ButtonSize
  leadingIcon?: IconDefinition
  trailingIcon?: IconDefinition
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  icon: 'size-6 rounded-base p-0 text-tSmall',
  small:
    'h-8 px-3 rounded-[var(--radius-base)] text-sm/5 font-semibold [&_[data-slot=button-icon]]:text-xs/4',
  medium:
    'h-10 px-4 rounded-[var(--radius-base)] text-sm/5 font-semibold [&_[data-slot=button-icon]]:text-base/5',
  large:
    'h-14 px-6 rounded-[var(--radius-base)] text-xl/7 font-medium [&_[data-slot=button-icon]]:text-xl/6',
}

const TONE_CLASSES: Record<ButtonTone, Record<ButtonVariant, string>> = {
  brand: {
    primary: 'bg-fill-brand text-text-inverse-strong',
    secondary: 'border border-stroke-brand-strong text-text-brand',
    tertiary: 'text-text-brand',
  },
  neutral: {
    primary: 'bg-fill-strong text-text-inverse-strong',
    secondary: 'border border-stroke-strong text-text-weak',
    tertiary: 'text-text-weak',
  },
  danger: {
    primary: 'bg-fill-error-strong text-text-inverse-strong',
    secondary: 'border border-stroke-error-strong text-text-error',
    tertiary: 'text-text-error',
  },
}

const ICON_TONE_CLASSES: Record<ButtonTone, string> = {
  brand: 'text-icon-brand',
  neutral: 'text-icon-neutral',
  danger: 'text-icon-error',
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'shadow-raised active:shadow-none',
  secondary: 'bg-transparent shadow-raised active:shadow-none',
  tertiary: 'bg-transparent',
}

const DISABLED_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-fill-disabled text-text-inverse-strong shadow-none',
  secondary: 'border border-stroke-disabled bg-transparent text-text-disabled shadow-none',
  tertiary: 'bg-transparent text-text-disabled shadow-none',
}

function joinClasses(...classes: Array<string | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

/**
 * Reusable Quave Run design-system button matching Figma ingredient set 4861:3278.
 */
export function Button({
  children,
  variant = 'primary',
  tone = 'brand',
  size = 'large',
  leadingIcon,
  trailingIcon,
  className,
  disabled = false,
  type = 'button',
  ref,
  ...rest
}: ButtonProps): ReactElement {
  const iconClassName = disabled
    ? variant === 'primary'
      ? 'text-icon-inverse-strong'
      : 'text-icon-disabled'
    : variant === 'primary'
      ? 'text-icon-inverse-strong'
      : ICON_TONE_CLASSES[tone]

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      data-variant={variant}
      data-tone={tone}
      data-size={size}
      className={joinClasses(
        'group relative isolate inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap py-0 transition-[background-color,box-shadow,color,border-color] duration-150',
        FOCUS_RING,
        size === 'icon' ? undefined : 'w-fit',
        "before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-[inherit] before:content-['']",
        'hover:before:bg-fill-hover active:before:bg-fill-press',
        'disabled:cursor-default disabled:hover:before:bg-transparent disabled:active:before:bg-transparent',
        SIZE_CLASSES[size],
        disabled ? DISABLED_CLASSES[variant] : TONE_CLASSES[tone][variant],
        !disabled ? VARIANT_CLASSES[variant] : undefined,
        className,
      )}
    >
      {leadingIcon ? (
        <span
          aria-hidden="true"
          data-slot="button-icon"
          className={joinClasses(
            'relative z-10 inline-flex shrink-0 items-center justify-center leading-none [&>svg]:size-[1em]',
            iconClassName,
          )}
        >
          <FontAwesomeIcon icon={leadingIcon} />
        </span>
      ) : null}
      {children !== undefined && children !== null ? (
        <span className="relative z-10 px-1">{children}</span>
      ) : null}
      {trailingIcon ? (
        <span
          aria-hidden="true"
          data-slot="button-icon"
          className={joinClasses(
            'relative z-10 inline-flex shrink-0 items-center justify-center leading-none [&>svg]:size-[1em]',
            iconClassName,
          )}
        >
          <FontAwesomeIcon icon={trailingIcon} />
        </span>
      ) : null}
    </button>
  )
}
